<template>
  <drop-down :menus="menus" :trigger="'click'">
    <icon icon="nav.svg" :width="36" :height="36" fill="none" />
    <div class="opacity-70 text-xs ml-[6px]">菜单</div>
  </drop-down>

  <modal-confirm-save ref="confirmSaveModal" />
  <modal-multiple-languages ref="multipleLanguagesModal" />
  <modal-watchface-power-class ref="watchfacePowerClassModal" />
  <modal-send-to-phone ref="phoneModal" />
  <modal-send-to-phone-old ref="phoneModalOld" />
  <modal-aod-image-valid ref="aodImageValidModal" />
</template>

<script setup lang="ts">
import { useSnapshot } from '@/hooks/use-snapshot';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import {
  status,
  useGlobalShortcuts,
  useWatchfaceManage,
  useWatchfaceSave,
  watchfaceExporting,
  watchfaceImageLoading,
  watchfaceOpening
} from '@/hooks';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { notification } from 'ant-design-vue';
import { PopupType, useEditorPopups } from '@/hooks/use-editor-popups';
import { IMAGE_FORMATS } from '@/resources/image-format';
import { canUse } from '@/utils/can-use.utils';
import { GlobalConfig } from '@/services/gloabl.config';
import { useWatchfaceAodPixel } from '@/hooks';
import { EventType, emitter } from '@/utils/emitter.utils';
import showToast from '../common/toast';
import { ElectronApi } from '@/services/electron.api';
import { useEditorStore } from '@/store/use-editor-store';

const emit = defineEmits<{
  (event: 'export'): void;
}>();
const router = useRouter();
const { undo, redo, history } = useSnapshot();
const { pickThenOpen } = useWatchfaceManage();
const { saveWatchfaceData } = useWatchfaceSave();

const watchfaceStore = useWatchfaceStore();
const editorStore = useEditorStore();

const confirmSaveModal = ref();
const multipleLanguagesModal = ref();
const watchfacePowerClassModal = ref();
const phoneModal = ref();
const phoneModalOld = ref();
const aodImageValidModal = ref();
const { canuseAODValid } = useWatchfaceAodPixel();
const updateCompression = async (
  key: 'imageCompression' | 'imageFormat',
  val: string | boolean
) => {
  await watchfaceStore.update({
    [key]: val
  });
  notification.info({
    message:
      key == 'imageCompression'
        ? `表盘文件压缩已${val ? '启用' : '关闭'}`
        : '图片压缩方式已修改为：' + val
  });
};

const menus = computed(() => {
  const { index = 0, max = 0, min = 0 } = history.value ?? {};
  const imageCompression =
    !!watchfaceStore.$state.description?.imageCompression;
  const imageFormat = watchfaceStore.$state.description?.imageFormat;

  const menus: DropDownMenuItem[] = [
    // {
    //   title: '返回首页',
    //   onClick: () => {
    //     confirmSaveIfNeed(() => router.replace('home'));
    //   }
    // },
    {
      title: '文件',
      children: [
        {
          title: '新建',
          onClick: () => {
            emitter.emit(EventType['modal:watchface-manage'], {
              mode: 'create'
            });
          }
        },
        {
          title: '打开',
          onClick: () => pickThenOpen()
        },
        {
          title: '编辑表盘信息',
          onClick: () => {
            emitter.emit(EventType['modal:watchface-manage'], {
              path: watchfaceStore.path,
              mode: 'edit'
            });
          }
        },
        {
          title: '保存',
          onClick: () => saveWatchfaceData(),
          shortcuts: ['CommandOrControl', 'S']
        }
      ]
    },
    {
      title: '编辑',
      children: [
        {
          title: '撤销',
          disabled: index <= min,
          onClick: () => undo(),
          shortcuts: ['CommandOrControl', 'Z']
        },
        {
          title: '取消撤销',
          disabled: index >= max,
          onClick: () => redo(),
          shortcuts: ['Shift', 'CommandOrControl', 'Z']
        }
      ]
    },
    {
      title: '视窗',
      children: [
        {
          title: '视窗背景颜色',
          onClick: () =>
            useEditorPopups().togglePopup(PopupType['bgColorPicker'])
        },
        {
          delimiter: true,
          title: 'd1'
        },
        {
          title: '表盘居中',
          onClick: () => {
            emitter.emit(EventType['workbench:action'], { action: 'center' });
          }
        },
        {
          delimiter: true
        },
        {
          title: '放大',
          shortcuts: ['+'],
          onClick: () => {
            emitter.emit(EventType['workbench:action'], { action: 'scaleUp' });
          }
        },
        {
          title: '缩小',
          shortcuts: ['-'],
          onClick: () => {
            emitter.emit(EventType['workbench:action'], {
              action: 'scaleDown'
            });
          }
        },
        {
          title: '缩放至100%',
          shortcuts: ['CommandOrControl', '0'],
          onClick: () => {
            emitter.emit(EventType['workbench:action'], {
              action: 'scaleTo100'
            });
          }
        }
      ]
    },
    // {
    //   title: '编辑',
    //   onClick: () => {
    //     createModalRef.value?.open?.({
    //       mode: 'edit',
    //       path: watchfaceStore.path
    //     });
    //   }
    // },
    {
      delimiter: true
    },
    {
      title: '压缩方式',
      children: [
        {
          title: '表盘压缩方式',
          hidden: GlobalConfig.adjustWatchfaceCompression ? false : true,
          children: [true, false].map((val) => {
            return {
              title: val ? '启用' : '关闭',
              selected: val == imageCompression,
              onClick: () => {
                updateCompression('imageCompression', val);
              }
            };
          })
        },
        {
          title: '图片压缩方式',
          hidden: GlobalConfig.autoChooseImageFormat ? true : false,
          children: IMAGE_FORMATS.map((val) => {
            return {
              title: val,
              selected: val == imageFormat,
              onClick: () => {
                updateCompression('imageFormat', val);
              }
            };
          })
        }
      ],
      hidden:
        (!GlobalConfig.adjustWatchfaceCompression &&
          GlobalConfig.autoChooseImageFormat) ||
        !canUse('compress').valid
    },
    {
      title: '多语言',
      hidden: canUse('multi-locals').valid ? false : true,
      onClick: () => {
        multipleLanguagesModal.value.open?.();
      }
    },
    {
      title: 'AOD校验工具',
      hidden: !canuseAODValid.value,
      onClick: () => {
        aodImageValidModal.value?.open?.();
      }
    },
    {
      title: '表盘功耗评级',
      hidden: canUse('power-class').valid ? false : true,
      onClick: () => {
        watchfacePowerClassModal.value.open?.();
      }
    },
    {
      delimiter: true
    },
    {
      title: '导出',
      onClick: () => {
        emit('export');
      }
    },
    {
      title: '固件端预览',
      disabled: watchfaceExporting.value,
      hidden: canUse('push-then-preview').valid ? false : true,
      onClick: () => {
        phoneModal.value?.open?.();
      }
    },
    {
      title: '固件端预览（旧）',
      disabled: watchfaceExporting.value,
      hidden: ElectronApi.isStaging() || import.meta.env.DEV ? false : true,
      onClick: () => {
        phoneModalOld.value?.open?.();
      }
    },
    {
      delimiter: true
    },
    {
      title: '版本信息',
      onClick: () => {
        emitter.emit(EventType['modal:open-app-version']);
      }
    },
    {
      title: '打开程序目录',
      hidden: import.meta.env.DEV ? false : true,
      onClick: async () => {
        ElectronApi.showItemInFolder(await ElectronApi.getPath('userData'));
      }
    },
    {
      delimiter: true
    },
    {
      title: '关闭',
      shortcuts: ['CommandOrControl', 'W'],
      onClick: close
    },
    {
      title: '打开应用目录',
      hidden: import.meta.env.DEV ? false : true,
      onClick: async () => {
        const appPath = await ElectronApi.getPath('userData');
        ElectronApi.showItemInFolder(appPath);
      }
    }
  ];
  return menus;
});

const close = () => {
  // 如果关闭时不为零，设置为零
  if (editorStore.layersScrollTop !== 0) {
    editorStore.layersScrollTop = 0;
  }
  // 如果正在打开则不响应
  if (watchfaceOpening.value || watchfaceImageLoading.value) {
    showToast('当前表盘正在打开，无法返回，请稍后重试', { duration: 3000 });
    return;
  }
  // 如果有modal正在显示，则不响应，防止返回后，modaL仍显示bug
  const modals = document.getElementsByClassName('ant-modal-wrap');
  const _modals = Array.prototype.slice.call(modals, 0) as HTMLDivElement[];
  if (_modals.some((_modal) => _modal.style.display != 'none')) return;

  if (status.value == 'unsaved') {
    confirmSaveModal.value?.open?.();
  } else {
    router.replace('home');
  }
};
useGlobalShortcuts([
  { keyboardKeys: ['CommandOrControl', 'W'], handler: close }
]);
</script>
